// frontend/src/utils/permissions.js

// Define platform-wide permissions
export const PERMISSIONS = {
  // Dashboard & Analytics
  VIEW_DASHBOARD: 'view_dashboard',
  VIEW_ANALYTICS: 'view_analytics',
  VIEW_REPORTS: 'view_reports',
  EXPORT_DATA: 'export_data',
  
  // Agency Management
  VIEW_AGENCY_SETTINGS: 'view_agency_settings',
  EDIT_AGENCY_SETTINGS: 'edit_agency_settings',
  MANAGE_AGENCY: 'manage_agency',
  
  // Client Management
  VIEW_CLIENTS: 'view_clients',
  CREATE_CLIENT: 'create_client',
  EDIT_CLIENT: 'edit_client',
  DELETE_CLIENT: 'delete_client',
  VIEW_PII: 'view_pii', // NEW: View Personally Identifiable Information
  
  // Account Management
  VIEW_ACCOUNTS: 'view_accounts',
  CREATE_ACCOUNT: 'create_account',
  EDIT_ACCOUNT: 'edit_account',
  DELETE_ACCOUNT: 'delete_account',
  
  // Task Management
  VIEW_TASKS: 'view_tasks',
  CREATE_TASK: 'create_task',
  EDIT_TASK: 'edit_task',
  DELETE_TASK: 'delete_task',
  MANAGE_TASKS: 'manage_tasks',
  
  // Email & Communications
  SEND_EMAIL: 'send_email',
  VIEW_TEMPLATES: 'view_templates',
  CREATE_TEMPLATE: 'create_template',
  EDIT_TEMPLATE: 'edit_template',
  DELETE_TEMPLATE: 'delete_template',
  MANAGE_EMAIL_TEMPLATES: 'manage_email_templates',
  MANAGE_EMAIL_CAMPAIGNS: 'manage_email_campaigns',
  
  // User & Role Management
  MANAGE_TEAM: 'manage_team',
  INVITE_USERS: 'invite_users',
  REMOVE_USERS: 'remove_users',
  MANAGE_ROLES: 'manage_roles',
  MANAGE_USERS: 'manage_users',
  INVITE_PORTAL_CONTACT: 'invite_portal_contact',
  
  // Content Management
  MANAGE_CONTENT: 'manage_content',
  MANAGE_DOCUMENTS: 'manage_documents',
  
  // Other
  SCHEDULE_MEETING: 'schedule_meeting',
  VIEW_CALENDAR: 'view_calendar',
  MANAGE_WORKFLOWS: 'manage_workflows',
  // Forms
  MANAGE_FORMS: 'manage_forms',
  VIEW_FORMS: 'view_forms',
  VIEW_FORM_SUBMISSIONS: 'view_form_submissions',
};

// Roles to permissions mapping
export const ROLE_PERMISSIONS = {
  admin: Object.values(PERMISSIONS),
  owner: Object.values(PERMISSIONS),
  agency_admin: Object.values(PERMISSIONS),
  manager: [
    PERMISSIONS.VIEW_DASHBOARD,
    PERMISSIONS.VIEW_ANALYTICS,
    PERMISSIONS.VIEW_REPORTS,
    PERMISSIONS.VIEW_AGENCY_SETTINGS,
    PERMISSIONS.VIEW_CLIENTS,
    PERMISSIONS.CREATE_CLIENT,
    PERMISSIONS.EDIT_CLIENT,
    PERMISSIONS.VIEW_ACCOUNTS,
    PERMISSIONS.CREATE_ACCOUNT,
    PERMISSIONS.EDIT_ACCOUNT,
    PERMISSIONS.VIEW_TEMPLATES,
    PERMISSIONS.MANAGE_TASKS,
    PERMISSIONS.VIEW_TASKS,
    PERMISSIONS.CREATE_TASK,
    PERMISSIONS.INVITE_PORTAL_CONTACT,
    PERMISSIONS.VIEW_CALENDAR,
    PERMISSIONS.SCHEDULE_MEETING,
  ],
  contributor: [
    PERMISSIONS.VIEW_DASHBOARD,
    PERMISSIONS.VIEW_CLIENTS,
    PERMISSIONS.CREATE_CLIENT,
    PERMISSIONS.EDIT_CLIENT,
    // NOTE: agency_user (contributor) has NO access to accounts
    // PERMISSIONS.VIEW_ACCOUNTS,     // REMOVED
    // PERMISSIONS.CREATE_ACCOUNT,    // REMOVED
    PERMISSIONS.VIEW_TEMPLATES,
    PERMISSIONS.VIEW_TASKS,
    PERMISSIONS.CREATE_TASK,
    PERMISSIONS.EDIT_TASK,
    PERMISSIONS.INVITE_PORTAL_CONTACT,
    PERMISSIONS.VIEW_CALENDAR,
  ],
  viewer: [
    PERMISSIONS.VIEW_DASHBOARD,
    PERMISSIONS.VIEW_CLIENTS,
    PERMISSIONS.VIEW_ACCOUNTS,
    PERMISSIONS.VIEW_TEMPLATES,
    PERMISSIONS.VIEW_TASKS,
    PERMISSIONS.VIEW_CALENDAR,
  ],
};

export function getUserPermissions(user, currentAgencyId) {
  if (!user) {
    // Verbose logging disabled for performance
    // console.log('[RBAC] No user provided');
    return new Set();
  }
  
  // Superadmin has all permissions
  if (user.isSuperadmin) {
    // Verbose logging disabled for performance
    // console.log('[RBAC] User is superadmin - granting all permissions');
    return new Set(Object.values(PERMISSIONS));
  }
  
  // If user is the agency creator/owner, grant all permissions
  if (user.isAgencyCreator || user.isAgencyOwner) {
    // Verbose logging disabled for performance
    // console.log('[RBAC] User is agency creator/owner - granting all permissions');
    return new Set(Object.values(PERMISSIONS));
  }
  
  // Check agency membership for role
  let role = 'viewer'; // default fallback
  let membership = null;
  
  // Verbose logging disabled for performance
  // console.log('[RBAC] Getting permissions for user:', {
  //   userId: user._id || user.id,
  //   currentAgencyId,
  //   userCurrentAgencyId: user.currentAgencyId,
  //   membershipsCount: user.agencyMemberships?.length || 0,
  //   memberships: user.agencyMemberships?.map(m => ({
  //     agencyId: m.agencyId?._id || m.agencyId,
  //     role: m.role,
  //     status: m.status
  //   }))
  // });
  
  if (user.agencyMemberships && Array.isArray(user.agencyMemberships) && user.agencyMemberships.length > 0) {
    // If no currentAgencyId, check if user has any agency_admin role first
    if (!currentAgencyId && user.agencyMemberships.length > 0) {
      membership = user.agencyMemberships.find(m => m.role === 'agency_admin' || m.role === 'superadmin');
      if (!membership) {
        membership = user.agencyMemberships[0]; // Fallback to first membership
      }
    } else if (currentAgencyId) {
      // Try to find membership by agencyId (handle both string and ObjectId)
      membership = user.agencyMemberships.find(
        m => {
          const mAgencyId = m.agencyId?._id || m.agencyId;
          return String(mAgencyId) === String(currentAgencyId);
        }
      );
      
      // If not found, try using the user's currentAgencyId if available
      if (!membership && user.currentAgencyId) {
        membership = user.agencyMemberships.find(
          m => {
            const mAgencyId = m.agencyId?._id || m.agencyId;
            return String(mAgencyId) === String(user.currentAgencyId);
          }
        );
      }
      
      // Final fallback: use first active membership
      if (!membership) {
        membership = user.agencyMemberships.find(m => m.status === 'active') || user.agencyMemberships[0];
      }
    } else {
      // No currentAgencyId at all, use first active membership or any membership
      membership = user.agencyMemberships.find(m => m.status === 'active') || user.agencyMemberships[0];
    }
    
    if (membership) {
      role = membership.role || 'viewer';
      console.log('[RBAC] Found membership:', {
        role,
        agencyId: membership.agencyId?._id || membership.agencyId,
        status: membership.status,
        hasPermissions: !!membership.permissions
      });
      console.log('[RBAC] User role in agency:', role, 'Agency ID:', currentAgencyId);
      
      // Agency admin has all permissions
      if (role === 'agency_admin' || role === 'superadmin') {
        console.log('[RBAC] User is agency_admin/superadmin - granting all permissions');
        return new Set(Object.values(PERMISSIONS));
      }

      // NEW: Use actual permissions from backend instead of hardcoded role mapping
      if (membership.permissions && typeof membership.permissions === 'object') {
        console.log('[RBAC] Using actual permissions from backend:', membership.permissions);
        
        // Map backend permission names to frontend PERMISSIONS constants (new style)
        const permissionMap = {
          // Agency management
          viewAgencySettings: PERMISSIONS.VIEW_AGENCY_SETTINGS,
          editAgencySettings: PERMISSIONS.EDIT_AGENCY_SETTINGS,
          manageAgency: PERMISSIONS.MANAGE_AGENCY,
          manageTeam: PERMISSIONS.MANAGE_TEAM,
          inviteUsers: PERMISSIONS.INVITE_USERS,
          removeUsers: PERMISSIONS.REMOVE_USERS,
          manageRoles: PERMISSIONS.MANAGE_ROLES,
          manageUsers: PERMISSIONS.MANAGE_USERS,
          // Clients
          viewClients: PERMISSIONS.VIEW_CLIENTS,
          createClient: PERMISSIONS.CREATE_CLIENT,
          editClient: PERMISSIONS.EDIT_CLIENT,
          deleteClient: PERMISSIONS.DELETE_CLIENT,
          viewPII: PERMISSIONS.VIEW_PII,
          // Accounts
          viewAccounts: PERMISSIONS.VIEW_ACCOUNTS,
          createAccount: PERMISSIONS.CREATE_ACCOUNT,
          editAccount: PERMISSIONS.EDIT_ACCOUNT,
          deleteAccount: PERMISSIONS.DELETE_ACCOUNT,
          // Tasks
          viewTasks: PERMISSIONS.VIEW_TASKS,
          createTask: PERMISSIONS.CREATE_TASK,
          editTask: PERMISSIONS.EDIT_TASK,
          deleteTask: PERMISSIONS.DELETE_TASK,
          manageTasks: PERMISSIONS.MANAGE_TASKS,
          // Templates & Email
          viewTemplates: PERMISSIONS.VIEW_TEMPLATES,
          createTemplate: PERMISSIONS.CREATE_TEMPLATE,
          editTemplate: PERMISSIONS.EDIT_TEMPLATE,
          deleteTemplate: PERMISSIONS.DELETE_TEMPLATE,
          sendEmail: PERMISSIONS.SEND_EMAIL,
          manageEmailTemplates: PERMISSIONS.MANAGE_EMAIL_TEMPLATES,
          manageEmailCampaigns: PERMISSIONS.MANAGE_EMAIL_CAMPAIGNS,
          // Analytics & Data
          viewAnalytics: PERMISSIONS.VIEW_ANALYTICS,
          viewReports: PERMISSIONS.VIEW_REPORTS,
          viewDashboard: PERMISSIONS.VIEW_DASHBOARD,
          exportData: PERMISSIONS.EXPORT_DATA,
          // Content
          manageContent: PERMISSIONS.MANAGE_CONTENT,
          manageDocuments: PERMISSIONS.MANAGE_DOCUMENTS,
          // Calendar
          viewCalendar: PERMISSIONS.VIEW_CALENDAR,
          scheduleMeeting: PERMISSIONS.SCHEDULE_MEETING,
          // Workflows
          manageWorkflows: PERMISSIONS.MANAGE_WORKFLOWS,
          // Portal
          invitePortalContact: PERMISSIONS.INVITE_PORTAL_CONTACT,
        };
        const perms = new Set();
        // New-style booleans
        for (const [backendPerm, frontendPerm] of Object.entries(permissionMap)) {
          if (membership.permissions[backendPerm] === true) {
            perms.add(frontendPerm);
          }
        }

        // Legacy can*-style booleans from CRMUser.permissions
        const can = membership.permissions;
        // Clients
        if (can.canManageClients) {
          perms.add(PERMISSIONS.VIEW_CLIENTS);
          perms.add(PERMISSIONS.CREATE_CLIENT);
          perms.add(PERMISSIONS.EDIT_CLIENT);
        }
        if (can.canViewPII) {
          perms.add(PERMISSIONS.VIEW_PII);
        }
        // Accounts
        if (can.canManageAccounts) {
          perms.add(PERMISSIONS.VIEW_ACCOUNTS);
          perms.add(PERMISSIONS.CREATE_ACCOUNT);
          perms.add(PERMISSIONS.EDIT_ACCOUNT);
        }
        // Tasks
        if (can.canEditTasks) {
          perms.add(PERMISSIONS.VIEW_TASKS);
          perms.add(PERMISSIONS.CREATE_TASK);
          perms.add(PERMISSIONS.EDIT_TASK);
          perms.add(PERMISSIONS.MANAGE_TASKS);
        }
        // Email & templates
        if (can.canSendEmail) perms.add(PERMISSIONS.SEND_EMAIL);
        if (can.canManageEmailTemplates) {
          perms.add(PERMISSIONS.VIEW_TEMPLATES);
          perms.add(PERMISSIONS.CREATE_TEMPLATE);
          perms.add(PERMISSIONS.EDIT_TEMPLATE);
          perms.add(PERMISSIONS.DELETE_TEMPLATE);
        }
        // Analytics & data
        if (can.canExportData) perms.add(PERMISSIONS.EXPORT_DATA);
        if (can.canViewReports) perms.add(PERMISSIONS.VIEW_REPORTS);
        // User & roles
        if (can.canManageRoles) perms.add(PERMISSIONS.MANAGE_ROLES);
        if (can.canManageUsers) perms.add(PERMISSIONS.MANAGE_USERS);
        // Agency
        if (can.canManageAgency) perms.add(PERMISSIONS.MANAGE_AGENCY);
        // Content
        if (can.canManageContent) perms.add(PERMISSIONS.MANAGE_CONTENT);

        console.log('[RBAC] Permissions granted from backend (merged):', Array.from(perms));
        return perms;
      }
    } else {
      // Only log warning if agency was expected (i.e., not undefined)
      if (currentAgencyId) {
        console.log('[RBAC] No membership found for agency:', currentAgencyId, 'User memberships:', user.agencyMemberships);
      }
      // If currentAgencyId is undefined, this is expected for users with no agency yet
    }
  } else {
    // Only log if we have a user (not expected for users with no memberships yet)
    if (user && user._id) {
      console.log('[RBAC] No agencyMemberships found on user');
    }
  }
  
  // FALLBACK: If no backend permissions found, use role-based mapping
  // If we found a membership but no permissions, use role-based mapping
  if (membership && role && role !== 'viewer') {
    console.log('[RBAC] Using role-based permissions for role:', role);
  } else if (!membership && user.agencyMemberships && user.agencyMemberships.length > 0) {
    // Try one more time to find membership using user's currentAgencyId
    const userAgencyId = user.currentAgencyId;
    if (userAgencyId) {
      membership = user.agencyMemberships.find(
        m => {
          const mAgencyId = m.agencyId?._id || m.agencyId;
          return String(mAgencyId) === String(userAgencyId);
        }
      );
      if (membership) {
        role = membership.role || 'viewer';
        console.log('[RBAC] Found membership using user.currentAgencyId:', role);
      }
    }
    
    // If still no membership, use first active or first membership
    if (!membership) {
      membership = user.agencyMemberships.find(m => m.status === 'active') || user.agencyMemberships[0];
      if (membership) {
        role = membership.role || 'viewer';
        console.log('[RBAC] Using first available membership:', role);
      }
    }
  }
  
  if (!membership) {
    console.warn('[RBAC] No membership found, using default viewer permissions');
    role = 'viewer';
  }
  
  // Map backend role to frontend role
  const roleMap = {
    'agency_admin': 'admin',
    'agency_user': 'contributor',
    'superadmin': 'admin',
    'account_contact': 'viewer',
    'client': 'viewer'
  };
  
  const mappedRole = roleMap[role] || 'viewer';
  const perms = new Set();
  (ROLE_PERMISSIONS[mappedRole] || ROLE_PERMISSIONS['viewer']).forEach((p) => perms.add(p));
  
  console.log('[RBAC] Final permissions granted:', {
    role,
    mappedRole,
    membershipFound: !!membership,
    permissionsCount: perms.size,
    permissions: Array.from(perms).slice(0, 10) // Show first 10 for brevity
  });
  return perms;
}

export function hasPermission(user, permission, currentAgencyId) {
  return getUserPermissions(user, currentAgencyId).has(permission);
}


